import React, { FC } from 'react';
import { makeAutoObservable } from 'mobx';
import PropTypes from 'prop-types';
import { DateTime } from "luxon";

export const AuthStoreContext = React.createContext<AuthStore>({} as AuthStore);
export class AuthStore {
  static localStorageKey = "access-token"

  isAuthenticated = false
  userName = '';
  userEmail = '';
  isAdmin = false
  isSuper = false
  /** The milliseconds since 1970 at which the token expires */
  expiry = DateTime.now()

  private expiryTimeout = 0

  get expired(): boolean {
    return this.expiry <= DateTime.now()
  }

  get millisecondsUntilExpiry(): number {
    return (+this.expiry - +new Date())
  }

  constructor() {
    this.restore()
    makeAutoObservable(this)
  }

  private restore(): void {
    const jsonString = localStorage.getItem(AuthStore.localStorageKey)
    if (!jsonString) { return }
    const data = JSON.parse(jsonString)
    if (data) {
      const expiryDate = DateTime.fromISO(data.expiry)
      this.userName = data.userName
      this.userEmail = data.userEmail
      this.expiry = expiryDate
      this.isSuper = data.isSuper
      this.isAdmin = data.isAdmin
      this.isAuthenticated = !this.expired
    }
  }

  private storeLocally(): void {
    const json = JSON.stringify(this, (key, value) => {
      switch (key) {
        case 'isAuthenticated':
        case 'expiryTimout':
          return undefined
          break;
        default:
          return value
      }
    })
    localStorage.setItem(AuthStore.localStorageKey, json)
  }

  private setExpiryInterval(): void {
    clearTimeout(this.expiryTimeout)
    this.expiryTimeout = window.setTimeout(() => {
      localStorage.removeItem(AuthStore.localStorageKey)
      this.isAuthenticated = false
    }, this.millisecondsUntilExpiry)
  }

  public setExpiresAt(date: DateTime): void {
    this.expiry = date
    this.storeLocally()
    this.setExpiryInterval()
  }

  public signIn(name: string, email: string, expiry: DateTime, isAdmin = false, isSuper = false): void {
    this.isAuthenticated = true
    this.userName = name
    this.userEmail = email
    this.expiry = expiry
    this.isAdmin = isAdmin
    this.isSuper = isSuper
    this.storeLocally
    this.setExpiryInterval()
  }

  public signOut(): void {
    this.isAuthenticated = false
    this.userName = '';
    this.userEmail = '';
  }
}

type Props = unknown;

export const AuthStoreProvider: FC<Props> = ({ children }) => {
  const store = new AuthStore();
  return (
    <AuthStoreContext.Provider value={store}>{children}</AuthStoreContext.Provider>
  )
}

AuthStoreProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuthStore = (): AuthStore => React.useContext(AuthStoreContext);
